import React, { useEffect, useState } from 'react'
import {
    Grid,
    Box,
    Typography,
    Button,
} from "@mui/material"
import FullScreenLoader from '../Common/FullScreenLoader'
import { ExternalLink, Disc3, Video, Scale, Link2, ListMusic, UserPlus, FileText, Ticket, ListChecks, Wallet, Music, LayoutGrid, Users, CheckCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { countMeterAdmin, getSubAdminTrackerStatsAdmin } from '../Common/actions'

const queueItems = [
    { label: 'Review Releases', countKey: 'in_review', path: '/releases/review', icon: <Disc3 size={16} /> },
    { label: 'Video Releases', countKey: 'video_releases', path: '/video_releases/review', icon: <Video size={16} /> },
    { label: 'UGC Claims', countKey: 'ugc_claim', path: '/ugc_claims', icon: <Scale size={16} /> },
    { label: 'Profile Linking', countKey: 'profile_linking', path: '/profile_linking', icon: <Link2 size={16} /> },
    { label: 'Playlist Pitching', countKey: 'pitch_playlist', path: '/playlist_pitching', icon: <ListMusic size={16} /> },
    { label: 'Label Requests', countKey: 'label_requests', path: '/label-requests', icon: <UserPlus size={16} /> },
    { label: 'Report Requests', countKey: 'reports_requested', path: '/report_requests', icon: <FileText size={16} /> },
    { label: 'Tickets', countKey: 'open_tickets', path: '/tickets', icon: <Ticket size={16} /> },
    { label: 'Whitelisting', countKey: 'whitelisting', path: '/whitelisting', icon: <ListChecks size={16} /> },
    { label: 'Requested Payouts', countKey: 'withdrawl_requested', path: '/payout', icon: <Wallet size={16} /> },
    { label: 'Lyrics Distribution', countKey: 'distribute_lyrics', path: '/lyrics_distribution', icon: <Music size={16} /> },
]

// Continuous green → yellow → red based on count relative to max
const getCardColors = (count, maxCount) => {
    const ratio = maxCount > 0 ? Math.min(count / maxCount, 1) : 0
    // Hue: 130 (green) → 45 (yellow) at 0.5 → 0 (red) at 1
    const hue = count === 0 ? 130 : (
        ratio <= 0.5
            ? 130 - (130 - 45) * (ratio / 0.5)
            : 45 - 45 * ((ratio - 0.5) / 0.5)
    )
    const h = Math.round(hue)
    return {
        accent: count === 0 ? 'hsl(130, 40%, 48%)' : `hsl(${h}, 42%, ${Math.round(50 - ratio * 6)}%)`,
        bg: `hsl(${h}, 20%, ${Math.round(20 - ratio * 2)}%)`,
        border: `hsl(${h}, 25%, ${Math.round(28 - ratio * 4)}%)`,
    }
}


const QueueChip = ({ item, count, maxCount, onClick }) => {
    const { accent } = getCardColors(count, maxCount)

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                backgroundColor: '#1A1D27',
                border: '1px solid #2E3247',
                borderRadius: '6px',
                px: 1.5,
                py: 0.8,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    borderColor: accent,
                    backgroundColor: '#1E2130',
                },
            }}
        >
            <Box sx={{ color: accent, display: 'flex', alignItems: 'center' }}>
                {React.cloneElement(item.icon, { size: 14 })}
            </Box>
            <Typography sx={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#64748B',
                whiteSpace: 'nowrap',
            }}>
                {item.label}
            </Typography>
            <Typography sx={{
                fontSize: '0.8rem',
                fontWeight: 800,
                color: count === 0 ? '#4A5068' : accent,
                ml: 0.3,
            }}>
                {count}
            </Typography>
        </Box>
    )
}

const TeamStatCard = ({ row, currentMonthLabel, prevMonthLabel }) => {
    const curDone = row.current_month_done || 0
    const prevDone = row.prev_month_done || 0

    return (
        <Box
            sx={{
                height: '100%',
                maxWidth: 600,
                position: 'relative',
                overflow: 'visible',
                mt: 0,
                backgroundColor: '#1A1D27',
                border: '1px solid #2E3247',
                borderRadius: '8px',
                p: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    borderColor: '#4A5068',
                    transform: 'translateY(-2px)',
                },
            }}
        >
            {/* Two-column layout: Image left, Info right */}
            <Box sx={{ display: 'flex', gap: 1.5, height: '100%' }}>
                {/* Left: Large avatar */}
                <Box sx={{ position: 'relative', flexShrink: 0, width: 130, height: 170, borderRadius: '10px', overflow: 'hidden', border: '3px solid #2E3247' }}>
                    {row.photo ? (
                        <img
                            src={row.photo}
                            alt={row.name}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                    ) : (
                        <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#6C63FF',
                        }}>
                            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: '#fff' }}>
                                {row.name ? row.name.charAt(0).toUpperCase() : '?'}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Right: Name + stat boxes stacked */}
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.8, justifyContent: 'flex-end' }}>
                    <Typography sx={{
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#F1F5F9',
                        wordBreak: 'break-word',
                        mb: 2,
                    }}>
                        {row.name}
                    </Typography>

                    {/* Current month */}
                    <Box sx={{ backgroundColor: 'rgba(34, 197, 94, 0.08)', borderRadius: '5px', p: 0.8 }}>
                        <Typography sx={{
                            fontSize: '0.58rem',
                            fontWeight: 600,
                            color: '#64748B',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            mb: 0.2,
                        }}>
                            {currentMonthLabel}
                        </Typography>
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#F1F5F9', lineHeight: 1.1 }}>
                            {curDone}
                            <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#64748B' }}>
                                {' '}Tickets done
                            </Typography>
                        </Typography>
                    </Box>

                    {/* Previous month */}
                    <Box sx={{ backgroundColor: '#141620', borderRadius: '5px', p: 0.6 }}>
                        <Typography sx={{
                            fontSize: '0.52rem',
                            fontWeight: 600,
                            color: '#64748B',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            mb: 0.1,
                        }}>
                            {prevMonthLabel}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#F1F5F9', lineHeight: 1.1 }}>
                            {prevDone}
                            <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 500, color: '#64748B' }}>
                                {' '}Tickets done
                            </Typography>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default function Dashboard(props) {
    const navigate = useNavigate()
    const [queueCounts, setQueueCounts] = useState({})
    const [teamStats, setTeamStats] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        document.title = 'ST | Stats';
        return () => { document.title = 'Fronic Media Admin'; };
    }, []);

    useEffect(() => {
        Promise.all([
            countMeterAdmin().then((res) => setQueueCounts(res.body || {})).catch(() => {}),
            getSubAdminTrackerStatsAdmin().then((res) => setTeamStats(res.body || [])).catch(() => {}),
        ]).finally(() => setLoading(false))
    }, [])

    const maxQueueCount = Math.max(...queueItems.map(i => queueCounts[i.countKey] || 0), 1)

    return (
        <>
            <FullScreenLoader open={loading} />
            {/* Team Activity */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Users size={14} color="#4A5068" />
                        <Typography sx={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: '#4A5068',
                        }}>
                            Team Activity
                        </Typography>
                    </Box>
                    <Button
                        onClick={() => window.open('/live-analytics', '_blank')}
                        startIcon={<ExternalLink size={14} />}
                        sx={{
                            color: '#F1F5F9',
                            backgroundColor: 'rgba(108, 99, 255, 0.12)',
                            border: '1px solid rgba(108, 99, 255, 0.25)',
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            letterSpacing: '0.01em',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(108, 99, 255, 0.22)',
                                borderColor: 'rgba(108, 99, 255, 0.4)',
                                boxShadow: '0 0 16px rgba(108, 99, 255, 0.15)',
                            },
                        }}
                    >
                        Open Live Analytics
                    </Button>
                </Box>
                {teamStats.length === 0 ? (
                    <Box sx={{
                        backgroundColor: '#1A1D27',
                        border: '1px solid #2E3247',
                        borderRadius: '8px',
                        px: 2.5,
                        py: 3,
                        textAlign: 'center',
                    }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>No data available</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{ pt: 4 }}>
                        {(() => {
                            const currentMonthLabel = moment().format('MMM YYYY')
                            const prevMonthLabel = moment().subtract(1, 'month').format('MMM YYYY')
                            return [...teamStats].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((row) => {
                                return (
                                    <Grid item xs={12} sm={6} md={3} key={row.subAdminId}>
                                        <TeamStatCard
                                            row={row}
                                            currentMonthLabel={currentMonthLabel}
                                            prevMonthLabel={prevMonthLabel}
                                        />
                                    </Grid>
                                )
                            })
                        })()}
                    </Grid>
                )}
            </Box>

            {/* Stats */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LayoutGrid size={13} color="#4A5068" />
                    <Typography sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#4A5068',
                    }}>
                        Pending Queues
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {queueItems.map((item) => (
                        <QueueChip
                            key={item.countKey}
                            item={item}
                            count={queueCounts[item.countKey] || 0}
                            maxCount={maxQueueCount}
                            onClick={() => navigate(item.path)}
                        />
                    ))}
                </Box>
            </Box>
        </>
    )
}
